import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import { WorkspaceIcon } from './icons';
import { ConnectionMark } from './your-connections/connection-mark';
import { useWorkspace } from './context';
import { ConnectionFeedback } from './connection-feedback';
import { ConnectSheetHost } from './connect-access/connect-sheet';
import { itemFor, type PipedreamApp } from './pipedream-catalogue';
import { useConnections } from './use-connections';
import { connectionAvailabilityMessage } from './connection-availability';
import { isConnecting } from './connection-api';
import { useIntlLocale } from './intl-locale';
import { ChatConnectionSetupRow, isChatConnectionSetup } from './chat-connection-setup';
export { ChatConnectionSetupCard, isChatConnectionSetup } from './chat-connection-setup';

export type ChatConnectionOffer = {
  status: 'ready';
  items: Array<{
    kind?: 'pipedream' | 'native_setup';
    settingsPath?: string;
    slug: string;
    name: string;
    description?: string;
    imageUrl?: string | null;
    enabled?: boolean;
  }>;
};

export function isChatConnectionOffer(value: unknown): value is ChatConnectionOffer {
  if (!value || typeof value !== 'object') return false;
  const offer = value as ChatConnectionOffer;
  return (
    offer.status === 'ready' &&
    Array.isArray(offer.items) &&
    offer.items.length <= 3 &&
    offer.items.every(
      (item) =>
        item &&
        (item.kind === undefined ||
          item.kind === 'pipedream' ||
          (item.kind === 'native_setup' &&
            isChatConnectionSetup({
              status: 'setup_required',
              settingsPath: item.settingsPath,
              service: item,
            }))) &&
        typeof item.slug === 'string' &&
        /^[a-z0-9_]{1,100}$/.test(item.slug) &&
        typeof item.name === 'string' &&
        item.name.length <= 120 &&
        (item.description === undefined || typeof item.description === 'string') &&
        (item.imageUrl === undefined ||
          item.imageUrl === null ||
          typeof item.imageUrl === 'string') &&
        (item.enabled === undefined || typeof item.enabled === 'boolean'),
    )
  );
}

export function ChatConnections({ offer }: { offer: ChatConnectionOffer }) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(pageCatalog);
  const connections = useConnections(ws.companyName);
  const { data, busy, activate } = connections;
  const setup = data?.pipedreamSetup ?? data?.setup;
  return (
    <section className="ws-chat-connections" aria-label={copy.chatTitle}>
      <ConnectionFeedback connections={connections} />
      <ConnectSheetHost connections={connections} companyName={ws.companyName} />
      <div className="ws-chat-connections-heading">
        <strong>{copy.chatTitle}</strong>
        <span>{copy.chatIntro}</span>
      </div>
      {setup && !setup.available && (
        <p className="ws-chat-connect-notice" role="alert">
          {connectionAvailabilityMessage(setup.reason, locale)}
        </p>
      )}
      {offer.items.map((entry) => {
        if (entry.kind === 'native_setup')
          return (
            <ChatConnectionSetupRow
              key={entry.slug}
              offer={{
                status: 'setup_required',
                settingsPath: entry.settingsPath!,
                service: entry,
              }}
            />
          );
        const app: PipedreamApp = {
          slug: entry.slug,
          name: entry.name,
          description: entry.description ?? '',
          img_src: entry.imageUrl,
        };
        const matching = data?.items.filter(
          (item) =>
            item.id === `pipedream:${app.slug}` &&
            (item.status === 'connected' || item.status === 'unavailable'),
        );
        const unavailable = Boolean(matching?.some((item) => item.status === 'unavailable'));
        const connected = Boolean(matching?.length) && !unavailable;
        const privateItem = itemFor(app, 'private');
        const canConnect = Boolean(setup?.available && entry.enabled !== false);
        const connecting = isConnecting(busy, privateItem);
        return (
          <div className="ws-chat-connect-row" key={app.slug}>
            <ConnectionMark
              name={app.name}
              src={app.img_src ?? null}
              state={connected ? 'connected' : unavailable ? 'unavailable' : null}
            />
            <div className="ws-chat-connect-copy">
              <strong>{app.name}</strong>
              <small>
                {unavailable
                  ? copy.chatUnavailable
                  : connected
                    ? matching!.some((item) => item.audience === 'private')
                      ? copy.chatConnectedYou
                      : formatCopy(copy.chatConnectedCompany, { company: ws.companyName })
                    : app.description || copy.chatFallbackBody}
              </small>
            </div>
            {matching?.length ? (
              <span
                className="ws-pipedream-connect ws-chat-connect-done"
                data-state={unavailable ? 'unavailable' : 'connected'}
              >
                {!unavailable && <WorkspaceIcon name="check" size={13} />}
                {unavailable ? copy.tileUnavailable : copy.tileConnected}
              </span>
            ) : (
              <button
                type="button"
                className="ws-pipedream-connect"
                data-state={connecting ? 'busy' : canConnect ? 'connect' : 'unavailable'}
                data-emphasis="primary"
                disabled={Boolean(busy) || !canConnect}
                onClick={() => activate(privateItem)}
              >
                {connecting && <span className="ws-pipedream-spinner" aria-hidden="true" />}
                {connecting
                  ? copy.tileConnecting
                  : !canConnect
                    ? copy.tileUnavailable
                    : copy.tileConnect}
              </button>
            )}
          </div>
        );
      })}
      {connections.notice && (
        <p className="ws-chat-connect-notice" role="status">
          {connections.notice}
        </p>
      )}
      {connections.connectLink && (
        <p className="ws-chat-connect-notice">
          {copy.chatPopupBlocked}{' '}
          <a href={connections.connectLink} target="_blank" rel="noreferrer">
            {copy.chatOpenAuthorization}
          </a>
        </p>
      )}
      <p className="ws-chat-connect-foot">{copy.chatFoot}</p>
    </section>
  );
}
